/**
 * "Vì sao lại là điểm này?" — spec 01 §11's *"Employee xem cách tính score của
 * mình"*, and its acceptance criteria, on the employee's own screen.
 *
 * `GET /api/v1/productivity/explain/{employeeId}`. **It is published in
 * `openapi.yaml` and is not mounted**: WP-J3 (Task 39) has not run, so the path
 * falls through to `notFoundHandler` and answers `404 WORKFORCE_NOT_FOUND` today.
 * That is not a special case in this file — it is the same branch as "the period has
 * not been scored", because from the employee's side both are "there is no
 * explanation to show", and neither is a number.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THREE RENDERINGS, AND THE FIRST ONE NEVER SHOWS A NUMBER
 *
 *  **Not scored / not available.** No score, no `0`, no placeholder digit. Spec 01
 *  §11's acceptance is *"Dữ liệu thiếu làm giảm confidence, KHÔNG tự động gán 0"* —
 *  the scorer refuses to write a zero for missing data, and a UI that renders an
 *  unscored period as `0` puts back at the last step exactly what Task 38 refused,
 *  in front of the person the number is about.
 *
 *  **Scored with low confidence.** The score **is** shown, with `confidence` and
 *  `data_completeness` as labelled badges and every `missing_data_warnings` line
 *  beside it. Confidence is never used to hide the number: a hidden number cannot
 *  be questioned, and §17 exists so that it can be.
 *
 *  **Scored.** Score, components, and the drivers both ways.
 *
 *  `limitations` is `minItems: 1` in the contract and is rendered in **all three**,
 *  because its guaranteed member is the sentence that the score must not be the sole
 *  basis for a disciplinary decision. There is no control on this panel that acts on
 *  a score, and spec 01 §11's acceptance forbids one: *"Không có nút tự động kỷ luật
 *  hoặc trừ lương."*
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * A component with `data_available: false` renders "không đủ dữ liệu" rather than
 * its `contribution`, for the same reason. Its weight is still shown — the person is
 * entitled to know what the formula would have counted.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { explainPath } from '../../api/paths';
import type { ScoreExplanation } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { FreshnessNote } from './FreshnessNote';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

/**
 * Below this the explanation is shown with its warnings in front of the number.
 *
 * **`0.5`, and it is not this file's number.** It is the predicate of
 * `wp_scores_low_confidence_idx` (migration 018), which that migration's own comment
 * calls "the threshold at which Master §13.3's missing-data warning must be shown, so
 * the index *is* the warning's query". A second, larger threshold invented here would
 * mean the screen called a score low-confidence that the database does not, and would
 * drift the first time either moved. `PARTIAL_CONFIDENCE_CEILING` in
 * `src/modules/scoring/score.ts` is `0.4` for the same reason: a period that was not
 * fully observed is *always* under this line.
 */
const LOW_CONFIDENCE = 0.5;

function percent(value: number | undefined): string | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  return `${String(Math.round(value * 100))}%`;
}

/**
 * The limitations block, rendered in every branch including the ones with no score.
 *
 * `fallback` is used when there is no explanation body to read them from — an
 * unscored period still needs the sentence, because the absence of a score is
 * exactly when somebody is most likely to treat "no data" as a verdict.
 */
function Limitations({
  lines,
  fallback,
}: {
  readonly lines: readonly string[];
  readonly fallback: string;
}): ReactNode {
  const rendered = lines.length > 0 ? lines : [fallback];
  return (
    <ul className="wp-list" data-testid="score-limitations">
      {rendered.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

export function ScorePanel({
  date,
  employeeId,
}: {
  readonly date: string;
  readonly employeeId: string;
}): ReactNode {
  const { t } = useI18n();
  const { status, data, error, reload } = useApiQuery<ScoreExplanation>(
    explainPath(employeeId, date, date),
  );

  const confidence = data?.confidence;
  const lowConfidence = confidence !== undefined && confidence < LOW_CONFIDENCE;

  return (
    <section className="wp-section" aria-labelledby="score-heading">
      <h2 className="wp-section__title" id="score-heading">
        {t('score.heading')}
      </h2>

      {status === 'loading' ? (
        <Skeleton rows={3} label={t('score.loading')} testId="score-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <>
          <ApiErrorNote
            error={error}
            onRetry={reload}
            subject={t('score.subject')}
            unavailableNote={t('score.notScored')}
            testId="score-error"
          />
          {/* The sentence travels with the absence, not only with the number. */}
          <p className="wp-small wp-muted">{t('score.limitations.heading')}</p>
          <Limitations lines={[]} fallback={t('score.limitation.default')} />
        </>
      ) : null}

      {data === null ? null : (
        <div className="wp-card" data-testid="score-card">
          <p className="wp-score" data-testid="score-value">
            <span className="wp-visually-hidden">{t('score.value')}: </span>
            {data.score.toFixed(1)}
          </p>
          <p className="wp-small wp-muted">
            {t('score.period', { from: data.period_start, to: data.period_end })}
            {' · '}
            {t('score.formula', { version: data.formula_version })}
          </p>

          {/* G9 / D-37. Above the confidence badges and above the low-confidence
              note, because it answers a different question and answering it second
              invites a reader to file it under the same heading: those describe what
              the run could SEE, and this says whether the run's inputs have MOVED
              since. It never hides, zeroes or annotates the number above. */}
          <FreshnessNote freshness={data.freshness} />

          <p className="wp-flags">
            <Badge
              severity={lowConfidence ? 'medium' : 'low'}
              icon={lowConfidence ? 'circle-alert' : 'circle-check'}
              label={t('score.confidence', { value: percent(confidence) ?? t('score.unknown') })}
              title={t('score.confidence.help')}
              testId="score-confidence"
            />
            <Badge
              severity="info"
              icon="info"
              label={t('score.completeness', {
                value: percent(data.data_completeness) ?? t('score.unknown'),
              })}
              title={t('score.completeness.help')}
              testId="score-completeness"
            />
          </p>

          {lowConfidence ? (
            <StatusNote
              severity="medium"
              title={t('score.lowConfidence.title')}
              testId="score-low-confidence"
            >
              <p>{t('score.lowConfidence.body')}</p>
            </StatusNote>
          ) : null}

          {(data.missing_data_warnings ?? []).length === 0 ? null : (
            <ul className="wp-list" data-testid="score-warnings">
              {(data.missing_data_warnings ?? []).map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}

          <h3 className="wp-section__title">{t('score.components')}</h3>
          <div className="wp-table-scroll">
            <table className="wp-table wp-table--fluid" data-testid="score-components">
              <thead>
                <tr>
                  <th scope="col">{t('score.col.component')}</th>
                  <th scope="col">{t('score.col.weight')}</th>
                  <th scope="col">{t('score.col.contribution')}</th>
                </tr>
              </thead>
              <tbody>
                {data.components.map((component) => (
                  <tr key={component.key} data-testid={`score-component-${component.key}`}>
                    <th scope="row">{component.label ?? component.key}</th>
                    <td className="wp-mono">{component.weight.toFixed(2)}</td>
                    <td className="wp-mono">
                      {component.data_available === false
                        ? t('score.noData')
                        : component.contribution.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(data.positive_drivers ?? []).length === 0 ? null : (
            <>
              <h3 className="wp-section__title">{t('score.positive')}</h3>
              <ul className="wp-list" data-testid="score-positive">
                {(data.positive_drivers ?? []).map((driver) => (
                  <li key={driver}>{driver}</li>
                ))}
              </ul>
            </>
          )}

          {(data.negative_drivers ?? []).length === 0 ? null : (
            <>
              <h3 className="wp-section__title">{t('score.negative')}</h3>
              <ul className="wp-list" data-testid="score-negative">
                {(data.negative_drivers ?? []).map((driver) => (
                  <li key={driver}>{driver}</li>
                ))}
              </ul>
            </>
          )}

          <h3 className="wp-section__title">{t('score.limitations.heading')}</h3>
          <Limitations lines={data.limitations} fallback={t('score.limitation.default')} />
        </div>
      )}
    </section>
  );
}
